import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { LayoutDashboard, FileText, Users, Building2, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const riskBadgeVariant = (risk) => {
  if (risk === 'high') return 'destructive';
  if (risk === 'medium') return 'secondary';
  return 'outline';
};

const actionGroupClass = 'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end';
const actionButtonClass = 'w-full sm:w-auto sm:min-w-[96px]';

export function AdminComplianceTab({ onTotalChange }) {
  const [overview, setOverview] = useState(null);
  const [usersPayload, setUsersPayload] = useState({ total: 0, page: 1, limit: 50, results: [] });
  const [logsPayload, setLogsPayload] = useState({ total: 0, page: 1, limit: 50, results: [] });
  const [listingsMonitor, setListingsMonitor] = useState({ summary: {}, flagged_listings: [], duplicate_candidates: [], per_user_listing_count: [] });
  const [loading, setLoading] = useState(false);

  const [forceVersion, setForceVersion] = useState('');
  const [forcing, setForcing] = useState(false);
  const [impactPreview, setImpactPreview] = useState(null);
  const [impactConfirmed, setImpactConfirmed] = useState(false);

  const [editContent, setEditContent] = useState('');
  const [publishing, setPublishing] = useState(false);

  const [acceptedFilter, setAcceptedFilter] = useState('all');
  const [activityLevelFilter, setActivityLevelFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  const [usersPage, setUsersPage] = useState(1);

  const [logsPage, setLogsPage] = useState(1);
  const [logsUserId, setLogsUserId] = useState('');
  const [logsIp, setLogsIp] = useState('');
  const [logsVersion, setLogsVersion] = useState('all');

  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);

  const fetchOverview = useCallback(async () => {
    const response = await axios.get(`${API_URL}/api/admin/tos/overview`);
    setOverview(response.data);
  }, []);

  const fetchUsers = useCallback(async (page = usersPage) => {
    const params = {
      page,
      limit: 50,
    };
    if (versionFilter !== 'all') params.tos_version = versionFilter;
    if (acceptedFilter === 'accepted') params.accepted = true;
    if (acceptedFilter === 'pending') params.accepted = false;
    if (countryFilter !== 'all') params.country = countryFilter;
    if (activityLevelFilter !== 'all') params.activity_level = activityLevelFilter;

    const response = await axios.get(`${API_URL}/api/admin/tos/users`, { params });
    setUsersPayload(response.data);
    onTotalChange?.(response.data?.total || 0);
    setUsersPage(page);
  }, [acceptedFilter, activityLevelFilter, countryFilter, usersPage, versionFilter, onTotalChange]);

  const fetchLogs = useCallback(async (page = logsPage) => {
    const params = {
      page,
      limit: 50,
    };
    if (logsUserId.trim()) params.user_id = logsUserId.trim();
    if (logsIp.trim()) params.ip_address = logsIp.trim();
    if (logsVersion !== 'all') params.version = logsVersion;

    const response = await axios.get(`${API_URL}/api/admin/tos/logs`, { params });
    setLogsPayload(response.data);
    setLogsPage(page);
  }, [logsIp, logsPage, logsUserId, logsVersion]);

  const fetchListingsMonitor = useCallback(async () => {
    const response = await axios.get(`${API_URL}/api/admin/tos/listings-monitor`);
    setListingsMonitor(response.data);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOverview(),
        fetchUsers(1),
        fetchLogs(1),
        fetchListingsMonitor(),
      ]);
    } catch {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [fetchListingsMonitor, fetchLogs, fetchOverview, fetchUsers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Pre-fill editor with current ToS content whenever overview loads
  useEffect(() => {
    if (overview?.tos_content && !editContent) {
      setEditContent(overview.tos_content);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview?.tos_content]);

  const handlePreviewImpact = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/tos/force-update-preview`);
      setImpactPreview(response.data);
      setForceVersion(response.data.target_version || '');
      setImpactConfirmed(false);
    } catch {
      toast.error('Failed to fetch impact preview');
    }
  };

  const handleForceUpdate = async () => {
    if (!impactPreview || !impactPreview.target_version) {
      toast.error('Preview impact before forcing update');
      return;
    }
    if (!impactConfirmed || impactPreview.target_version !== forceVersion.trim()) {
      toast.error('Preview impact and confirm before forcing update');
      return;
    }
    setForcing(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/tos/force-update`, {
        new_version: impactPreview.target_version,
        apply_to_all_users: true,
      });
      const affected = response.data?.affected_user_count ?? response.data?.updated_users;
      const countText = typeof affected === 'number' ? `${affected} users affected.` : '';
      toast.success(`${countText} Users will be required to re-accept the Terms on their next request`.trim());
      setImpactPreview(null);
      setImpactConfirmed(false);
      await fetchAll();
    } catch {
      toast.error('Failed to force ToS update');
    } finally {
      setForcing(false);
    }
  };

  const handlePublishTos = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) {
      toast.error('Content cannot be empty');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm('This will force all users to re-accept the Terms. Continue?')) return;
    setPublishing(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/tos/update`, { content: trimmed });
      const { version, affected_user_count: affected } = response.data;
      toast.success(`ToS updated to ${version}. ${affected} user${affected !== 1 ? 's' : ''} must re-accept.`);
      await fetchAll();
    } catch {
      toast.error('Failed to publish ToS update');
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenDrilldown = async (userId) => {
    setLoadingDrilldown(true);
    setDrilldownOpen(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/tos/users/${userId}/drilldown`);
      setDrilldown(response.data);
    } catch {
      toast.error('Failed to load user drilldown');
      setDrilldownOpen(false);
    } finally {
      setLoadingDrilldown(false);
    }
  };

  const handleToggleSuspend = async (userId, isSuspended) => {
    try {
      // isSuspended param is the desired new is_active state
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { is_active: isSuspended });
      toast.success(isSuspended ? 'User unsuspended' : 'User suspended');
      await fetchUsers(usersPage);
    } catch {
      toast.error(isSuspended ? 'Failed to unsuspend user' : 'Failed to suspend user');
    }
  };

  const trendMax = useMemo(() => {
    const values = (overview?.acceptance_trend || []).map((entry) => entry.count || 0);
    return values.length ? Math.max(...values, 1) : 1;
  }, [overview]);

  const knownCountries = useMemo(() => {
    const set = new Set();
    (usersPayload.results || []).forEach((row) => {
      if (row.country) set.add(row.country);
    });
    return Array.from(set).sort();
  }, [usersPayload.results]);

  const nonCompliantUsers = useMemo(
    () => (usersPayload.results || []).filter((row) => !row.accepted),
    [usersPayload.results],
  );

  const highRiskUsers = useMemo(
    () => (usersPayload.results || []).filter((row) => row.risk_flag === 'high'),
    [usersPayload.results],
  );

  return (
    <div className="space-y-4">
      {/* Summary KPIs — always visible */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-sm">Accepted latest</CardTitle></CardHeader>
          <CardContent className="text-2xl font-semibold">{overview?.accepted_latest_percent ?? 0}%</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-sm">Pending users</CardTitle></CardHeader>
          <CardContent className="text-2xl font-semibold">{overview?.users_pending_acceptance ?? 0}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-sm">Total users</CardTitle></CardHeader>
          <CardContent className="text-2xl font-semibold">{overview?.total_users ?? 0}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-sm">Required version</CardTitle></CardHeader>
          <CardContent className="text-2xl font-semibold">{overview?.required_version || '—'}</CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <div className="w-full overflow-x-auto pb-1">
          <TabsList className="inline-flex min-w-max h-auto gap-1 sm:min-w-0 sm:flex-wrap">
          <TabsTrigger value="overview" className="flex items-center gap-1.5">
            <LayoutDashboard className="h-3.5 w-3.5" />Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />Users
            {nonCompliantUsers.length > 0 && (
              <Badge variant="destructive" className="ml-1.5 px-1.5 py-0 text-xs">{nonCompliantUsers.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="terms" className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />Terms
          </TabsTrigger>
          <TabsTrigger value="listings" className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5" />Listings
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />Logs
          </TabsTrigger>
          </TabsList>
        </div>

        {/* ── OVERVIEW ── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Current ToS Status</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p><strong>Current version:</strong> {overview?.current_version || overview?.required_version || '—'}</p>
              <p><strong>Required version:</strong> {overview?.required_version || '—'}</p>
              <p className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
                <strong className="shrink-0">Current hash:</strong>
                <span className="break-all font-mono text-xs sm:text-sm">{overview?.tos_hash || '—'}</span>
              </p>
              <p><strong>Last updated:</strong> {overview?.tos_last_updated_at || '—'}</p>
              <p><strong>Users on latest:</strong> {overview?.accepted_latest_percent ?? 0}%</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Acceptance Trend (30 days)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(overview?.acceptance_trend || []).slice(-10).map((entry) => (
                <div key={entry.date} className="flex items-center gap-3">
                  <div className="w-24 text-xs text-muted-foreground">{entry.date}</div>
                  <div className="h-2 rounded bg-muted flex-1 overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${Math.max(4, (entry.count / trendMax) * 100)}%` }} />
                  </div>
                  <div className="w-8 text-xs text-right">{entry.count}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Version Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>%</TableHead>
                      <TableHead>Last accepted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(overview?.version_breakdown || []).map((row) => (
                      <TableRow key={row.version}>
                        <TableCell>{row.version}</TableCell>
                        <TableCell>{row.user_count}</TableCell>
                        <TableCell>{row.percentage}%</TableCell>
                        <TableCell>{row.last_accepted_at || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TERMS ── */}
        <TabsContent value="terms" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Current Content</CardTitle></CardHeader>
            <CardContent>
              <textarea
                readOnly
                value={overview?.tos_content || ''}
                className="w-full min-h-[200px] rounded-md border border-border bg-muted/20 p-3 text-xs leading-relaxed"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Edit &amp; Publish</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-64 rounded-md border border-border bg-background p-3 text-sm leading-relaxed font-mono resize-y"
              />
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => setEditContent(overview?.tos_content || '')}
                  disabled={publishing}
                >
                  Reset
                </Button>
                <Button className="w-full sm:w-auto" onClick={handlePublishTos} disabled={publishing}>
                  {publishing ? 'Publishing…' : 'Publish'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Force Re-Accept (version bump only)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={forceVersion} readOnly placeholder="Preview to generate next version" />
                <Button variant="outline" onClick={handlePreviewImpact}>Preview impact</Button>
                <Button onClick={handleForceUpdate} disabled={forcing}>{forcing ? 'Updating…' : 'Force Update'}</Button>
              </div>
              {impactPreview && (
                <div className="rounded-md border p-3 text-sm space-y-1 bg-muted/20">
                  <p><strong>Current version:</strong> {impactPreview.current_version}</p>
                  <p><strong>Target version:</strong> {impactPreview.target_version}</p>
                  <p><strong>Affected users:</strong> {impactPreview.affected_user_count} ({impactPreview.affected_percentage}%)</p>
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={impactConfirmed}
                      onChange={(e) => setImpactConfirmed(e.target.checked)}
                    />
                    <span>I confirm this version bump will force re-acceptance.</span>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── USERS ── */}
        <TabsContent value="users" className="space-y-4 mt-4">
          {nonCompliantUsers.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Non-Compliant Users ({nonCompliantUsers.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Last active</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Risk</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {nonCompliantUsers.map((row) => (
                        <TableRow key={`nc-${row.id}`}>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.role}</TableCell>
                          <TableCell>{row.last_active || '—'}</TableCell>
                          <TableCell>{row.country || '—'}</TableCell>
                          <TableCell><Badge variant={riskBadgeVariant(row.risk_flag)}>{row.risk_flag}</Badge></TableCell>
                          <TableCell>
                            <div className={actionGroupClass}>
                              <Button className={actionButtonClass} size="sm" variant="outline" onClick={() => handleOpenDrilldown(row.id)}>View</Button>
                              <Button
                                className={actionButtonClass}
                                size="sm"
                                variant={row.is_active === false ? 'outline' : 'destructive'}
                                onClick={() => handleToggleSuspend(row.id, !row.is_active)}
                              >
                                {row.is_active === false ? 'Unsuspend' : 'Suspend'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>High Risk Users ({highRiskUsers.length})</CardTitle></CardHeader>
            <CardContent>
              {highRiskUsers.length > 0 ? (
                <div className="w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Risk reasons</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {highRiskUsers.map((row) => (
                        <TableRow key={`risk-${row.id}`}>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.role}</TableCell>
                          <TableCell>{(row.risk_reasons || []).join(', ') || '—'}</TableCell>
                          <TableCell>
                            <div className={actionGroupClass}>
                              <Button className={actionButtonClass} size="sm" variant="outline" onClick={() => handleOpenDrilldown(row.id)}>View</Button>
                              <Button
                                className={actionButtonClass}
                                size="sm"
                                variant={row.is_active === false ? 'outline' : 'destructive'}
                                onClick={() => handleToggleSuspend(row.id, !row.is_active)}
                              >
                                {row.is_active === false ? 'Unsuspend' : 'Suspend'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No high-risk users.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>All Users</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Select value={acceptedFilter} onValueChange={setAcceptedFilter}>
                  <SelectTrigger><SelectValue placeholder="Acceptance" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="pending">Not accepted</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={activityLevelFilter} onValueChange={setActivityLevelFilter}>
                  <SelectTrigger><SelectValue placeholder="Activity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All activity</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All countries</SelectItem>
                    {knownCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Input
                  value={versionFilter === 'all' ? '' : versionFilter}
                  onChange={(e) => setVersionFilter(e.target.value || 'all')}
                  placeholder="ToS version"
                />
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button className="w-full sm:w-auto" variant="outline" onClick={() => fetchUsers(1)}>Apply filters</Button>
                <Button className="w-full sm:w-auto" variant="ghost" onClick={() => { setAcceptedFilter('all'); setActivityLevelFilter('all'); setCountryFilter('all'); setVersionFilter('all'); }}>Reset</Button>
              </div>

              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>ToS Version</TableHead>
                      <TableHead>Accepted at</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Last active</TableHead>
                      <TableHead>Risk</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(usersPayload.results || []).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.role}</TableCell>
                        <TableCell>{row.tos_version || '—'}</TableCell>
                        <TableCell>{row.accepted_at || '—'}</TableCell>
                        <TableCell>{row.country || '—'}</TableCell>
                        <TableCell>{row.last_active || '—'}</TableCell>
                        <TableCell><Badge variant={riskBadgeVariant(row.risk_flag)}>{row.risk_flag}</Badge></TableCell>
                        <TableCell>
                          <div className={actionGroupClass}>
                            <Button className={actionButtonClass} size="sm" variant="outline" onClick={() => handleOpenDrilldown(row.id)}>View</Button>
                            <Button
                              className={actionButtonClass}
                              size="sm"
                              variant={row.is_active === false ? 'outline' : 'destructive'}
                              onClick={() => handleToggleSuspend(row.id, !row.is_active)}
                            >
                              {row.is_active === false ? 'Unsuspend' : 'Suspend'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button className="w-full sm:w-auto" variant="outline" disabled={usersPage <= 1} onClick={() => fetchUsers(usersPage - 1)}>Prev</Button>
                <Button className="w-full sm:w-auto" variant="outline" disabled={(usersPage * (usersPayload.limit || 50)) >= (usersPayload.total || 0)} onClick={() => fetchUsers(usersPage + 1)}>Next</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── LISTINGS ── */}
        <TabsContent value="listings" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Listings Monitoring</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <Badge variant="outline">Flags: {listingsMonitor?.summary?.total_flags ?? 0}</Badge>
                <Badge variant="outline">Duplicates: {listingsMonitor?.summary?.duplicates ?? 0}</Badge>
                <Badge variant="outline">Removals: {listingsMonitor?.summary?.removals ?? 0}</Badge>
              </div>

              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Listing count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(listingsMonitor?.per_user_listing_count || []).slice(0, 10).map((row) => (
                      <TableRow key={row.user_id}>
                        <TableCell>{row.email || row.user_id}</TableCell>
                        <TableCell>{row.listing_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── LOGS ── */}
        <TabsContent value="logs" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Acceptance Logs</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input value={logsUserId} onChange={(e) => setLogsUserId(e.target.value)} placeholder="Filter by user_id" />
                <Input value={logsIp} onChange={(e) => setLogsIp(e.target.value)} placeholder="Filter by IP" />
                <Input value={logsVersion === 'all' ? '' : logsVersion} onChange={(e) => setLogsVersion(e.target.value || 'all')} placeholder="Filter by version" />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button className="w-full sm:w-auto" variant="outline" onClick={() => fetchLogs(1)}>Apply</Button>
                <Button className="w-full sm:w-auto" variant="ghost" onClick={() => { setLogsUserId(''); setLogsIp(''); setLogsVersion('all'); }}>Reset</Button>
              </div>

              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Hash</TableHead>
                      <TableHead>Accepted at</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(logsPayload.results || []).map((row) => (
                      <TableRow key={row.id || `${row.user_id}-${row.accepted_at}`}>
                        <TableCell>{row.email || row.user_id}</TableCell>
                        <TableCell>{row.tos_version}</TableCell>
                        <TableCell className="max-w-[240px] truncate">{row.tos_hash || '—'}</TableCell>
                        <TableCell>{row.accepted_at}</TableCell>
                        <TableCell>{row.ip_address || '—'}</TableCell>
                        <TableCell>{row.country || '—'}</TableCell>
                        <TableCell>{row.device_type || '—'}</TableCell>
                        <TableCell>{row.source || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button className="w-full sm:w-auto" variant="outline" disabled={logsPage <= 1} onClick={() => fetchLogs(logsPage - 1)}>Prev</Button>
                <Button className="w-full sm:w-auto" variant="outline" disabled={(logsPage * (logsPayload.limit || 50)) >= (logsPayload.total || 0)} onClick={() => fetchLogs(logsPage + 1)}>Next</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Drilldown dialog — shared across tabs */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Compliance Drilldown</DialogTitle>
          </DialogHeader>
          {loadingDrilldown && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loadingDrilldown && drilldown && (
            <div className="space-y-4 text-sm">
              <p><strong>User:</strong> {drilldown.user?.email}</p>
              <p><strong>ToS history entries:</strong> {drilldown.tos_history?.length || 0}</p>
              <p><strong>Activity logs:</strong> {drilldown.activity_logs?.length || 0}</p>
              <p><strong>Listings:</strong> {drilldown.listings?.length || 0}</p>
              <p><strong>Document sends:</strong> {drilldown.document_sends?.length || 0}</p>
              <p><strong>Messages sent:</strong> {drilldown.messages?.length || 0}</p>

              <div className="rounded-md border p-3">
                <p className="font-medium mb-2">Acceptance history</p>
                <div className="w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Accepted at</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Hash</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Device</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(drilldown.tos_history || []).slice(0, 20).map((row) => (
                        <TableRow key={row.id || `${row.accepted_at}-${row.ip_address}`}>
                          <TableCell>{row.accepted_at || '—'}</TableCell>
                          <TableCell>{row.tos_version || '—'}</TableCell>
                          <TableCell className="max-w-[260px] truncate">{row.tos_hash || '—'}</TableCell>
                          <TableCell>{row.ip_address || '—'}</TableCell>
                          <TableCell>{row.country || '—'}</TableCell>
                          <TableCell>{row.device_type || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {loading && <p className="text-sm text-muted-foreground">Loading compliance data…</p>}
    </div>
  );
}
